import feathersClient from '../../feathersClient'

import { buildCommentQuery, buildReportQuery } from './chapterDiscussions.model'

import type {
  BlockDiscussionUserRequest,
  ChapterComment,
  ChapterCommentReport,
  CursorPage,
  DiscussionActionResponse,
  DiscussionBulkDeleteJobResponse,
  DiscussionCommentDetail,
  DiscussionCommentListItem,
  DiscussionContentActionRequest,
  DiscussionCsCommentIdentity,
  DiscussionReportActionRequest,
  DiscussionReportListItem,
  DiscussionUserEnforcement,
  ListDiscussionCommentsQuery,
  ListDiscussionReportsQuery,
  RetryBulkDeleteJobRequest,
  UnblockDiscussionUserRequest,
} from './chapterDiscussions.types'

const SERVICE_ROOT = 'admin/chapter-discussions'

interface DiscussionService {
  find(params?: { query?: Record<string, unknown> }): Promise<unknown>
  create(
    data: Record<string, unknown>,
    params?: { headers?: Record<string, string> },
  ): Promise<unknown>
}

interface DiscussionClient {
  service(path: string): Partial<DiscussionService>
}

export class DiscussionCsApiError extends Error {
  status?: number
  errorCode?: string
  current?: Record<string, unknown>

  constructor(input: {
    message: string
    status?: number
    errorCode?: string
    current?: Record<string, unknown>
  }) {
    super(input.message)
    this.name = 'DiscussionCsApiError'
    this.status = input.status
    this.errorCode = input.errorCode
    this.current = input.current
  }
}

function defaultRequestId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID()
  }
  return `discussion-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function normalizeError(error: unknown): DiscussionCsApiError {
  if (error instanceof DiscussionCsApiError) return error
  const value = error as {
    code?: number
    message?: string
    data?: {
      code?: string
      message?: string
      current?: Record<string, unknown>
    }
  }
  return new DiscussionCsApiError({
    message:
      value.data?.message ?? value.message ?? 'Discussion request failed',
    status: value.code,
    errorCode: value.data?.code,
    current: value.data?.current,
  })
}

function identityPath(identity: DiscussionCsCommentIdentity): string {
  return [
    identity.mediaType,
    identity.bookId,
    identity.chapterId,
    encodeURIComponent(identity.commentId),
  ].join('/')
}

export function createDiscussionCsApi(
  client: DiscussionClient,
  requestId: () => string = defaultRequestId,
) {
  const find = async <T>(
    path: string,
    query?: Record<string, unknown>,
  ): Promise<T> => {
    try {
      const service = client.service(path)
      if (!service.find) throw new Error(`Find is unavailable for ${path}`)
      return (await service.find(query ? { query } : undefined)) as T
    } catch (error) {
      throw normalizeError(error)
    }
  }

  const create = async <T>(
    path: string,
    data: Record<string, unknown>,
  ): Promise<T> => {
    try {
      const service = client.service(path)
      if (!service.create) throw new Error(`Create is unavailable for ${path}`)
      return (await service.create(data, {
        headers: { 'x-request-id': requestId() },
      })) as T
    } catch (error) {
      throw normalizeError(error)
    }
  }

  return {
    listComments(query: ListDiscussionCommentsQuery) {
      return find<CursorPage<DiscussionCommentListItem>>(
        `${SERVICE_ROOT}/comments`,
        buildCommentQuery(query) as Record<string, unknown>,
      )
    },
    getComment(identity: DiscussionCsCommentIdentity) {
      return find<DiscussionCommentDetail>(
        `${SERVICE_ROOT}/comments/${identityPath(identity)}`,
      )
    },
    createContentAction(
      identity: DiscussionCsCommentIdentity,
      data: DiscussionContentActionRequest,
    ) {
      return create<DiscussionActionResponse<ChapterComment>>(
        `${SERVICE_ROOT}/comments/${identityPath(identity)}/actions`,
        data as unknown as Record<string, unknown>,
      )
    },
    listReports(query: ListDiscussionReportsQuery) {
      return find<CursorPage<DiscussionReportListItem>>(
        `${SERVICE_ROOT}/reports`,
        buildReportQuery(query) as Record<string, unknown>,
      )
    },
    createReportAction(
      identity: DiscussionCsCommentIdentity,
      reportId: string,
      data: DiscussionReportActionRequest,
    ) {
      return create<DiscussionActionResponse<ChapterCommentReport>>(
        `${SERVICE_ROOT}/reports/${identityPath(identity)}/${encodeURIComponent(
          reportId,
        )}/actions`,
        data as unknown as Record<string, unknown>,
      )
    },
    blockUser(userId: number, data: BlockDiscussionUserRequest) {
      return create<DiscussionActionResponse<DiscussionUserEnforcement>>(
        `${SERVICE_ROOT}/users/${userId}/block`,
        data as unknown as Record<string, unknown>,
      )
    },
    unblockUser(userId: number, data: UnblockDiscussionUserRequest) {
      return create<DiscussionActionResponse<DiscussionUserEnforcement>>(
        `${SERVICE_ROOT}/users/${userId}/unblock`,
        data as unknown as Record<string, unknown>,
      )
    },
    getBulkDeleteJob(jobId: string) {
      return find<DiscussionBulkDeleteJobResponse>(
        `${SERVICE_ROOT}/bulk-delete-jobs/${encodeURIComponent(jobId)}`,
      )
    },
    retryBulkDeleteJob(jobId: string, data: RetryBulkDeleteJobRequest) {
      return create<DiscussionBulkDeleteJobResponse>(
        `${SERVICE_ROOT}/bulk-delete-jobs/${encodeURIComponent(jobId)}/retry`,
        data as unknown as Record<string, unknown>,
      )
    },
  }
}

const authenticatedClient = feathersClient.get()
if (!authenticatedClient) {
  throw new Error('Authenticated Feathers client is unavailable')
}

export const discussionCsApi = createDiscussionCsApi(authenticatedClient)
